import { createContext, useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * "No overlay opens on top of another" (DESIGN.md § Elevation & Depth:
 * "Modal on modal is forbidden... there is exactly one overlay layer and
 * the scrim is proof of it"), made a structural guarantee rather than a
 * convention nobody violates yet (Story 2.6 spec, Guards this story should
 * add): every overlay primitive (`Dialog`, `Sheet`, `Popover`,
 * `DropdownMenu`) registers with one shared gate before it is allowed to
 * actually render open, so a second `open` request while one is already
 * granted is refused rather than rendered.
 *
 * `createOverlayGate` (rather than one hard-coded module-level mutable
 * variable) exists so this file's own tests can exercise a fresh, isolated
 * gate per test — `appOverlayGate` below is the one real instance every
 * primitive in the running app shares.
 */
export interface OverlayGate {
  /** Grants the slot to `token` if nobody else holds it (or `token`
   * already does — the same caller re-requesting is not a second
   * overlay). Returns whether the request was granted. */
  requestOpen(token: object): boolean;
  /** Releases the slot if `token` is the current holder. A release from a
   * token that never held it (or already lost it) is a no-op. */
  release(token: object): void;
  /** Whether anything currently holds the slot. */
  isOccupied(): boolean;
  /** Notified whenever the slot's occupancy changes — a grant, a release,
   * or a refusal-worthy attempt. `useOverlayGrant` subscribes so a request
   * refused because something else held the gate retries the instant that
   * something else lets go, rather than staying refused forever (found
   * live: without this, a popover refused while a confirm dialog was open
   * stayed permanently closed even after the dialog closed, because
   * nothing ever prompted it to ask again). */
  subscribe(listener: () => void): () => void;
}

export function createOverlayGate(): OverlayGate {
  let owner: object | null = null;
  const listeners = new Set<() => void>();

  function emit(): void {
    listeners.forEach((listener) => listener());
  }

  return {
    requestOpen(token) {
      if (owner !== null && owner !== token) {
        return false;
      }
      const changed = owner !== token;
      owner = token;
      if (changed) {
        emit();
      }
      return true;
    },
    release(token) {
      if (owner === token) {
        owner = null;
        emit();
      }
    },
    isOccupied() {
      return owner !== null;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The one gate every real overlay primitive in the app shares. */
export const appOverlayGate: OverlayGate = createOverlayGate();

/**
 * Story 15.3 (`AddLeaseDialog.tsx`'s tenant picker, this codebase's first
 * real `Combobox` consumer): `Dialog`/`Sheet` provide `true` around their
 * own `children` once open — signals to a `Popover` rendered inside that
 * already-granted content (e.g. `Combobox`'s own floating listbox) that it
 * is a visual *child* of the one overlay already holding the gate, not a
 * second, independent overlay competing for it. DESIGN.md's "no overlay
 * opens on top of another" (`appOverlayGate`'s own module docstring) is
 * about two *independent* top-level overlays — a dialog opening while
 * another dialog is already open, or a standalone popover ("Save this
 * view") triggered from ordinary page content while a dialog covers it —
 * never about a dropdown-like control that only ever exists *inside* one
 * dialog's own content, the same way a plain native `<select>` already
 * opens its own options list from inside a `Dialog` without needing the
 * gate's permission at all (a `Combobox` is that same idea, built on
 * `Popover` because no native primitive offers type-to-filter). Without
 * this, the gate refuses the nested `Popover` for as long as its own
 * ancestor `Dialog`/`Sheet` remains open — confirmed live: a `Combobox`
 * rendered inside `AddLeaseDialog.tsx` never opened its own listbox at
 * all, not only in this story's own tests, until this fix.
 *
 * **What this gives up.** Before this fix, `appOverlayGate` was a genuine
 * structural guarantee across *every* registered overlay, nested or not —
 * this module's own original docstring called that out explicitly ("a
 * structural guarantee rather than a convention nobody violates yet").
 * Every `Popover`/`DropdownMenu` instance nested inside a granted
 * `Dialog`/`Sheet` now bypasses `appOverlayGate` entirely (`open` drives
 * `granted` directly) — which also means the gate no longer coordinates
 * between *sibling* nested overlays under the same ancestor (e.g.
 * `AddLeaseDialog.tsx`'s own repeatable tenant picker, several `Combobox`/
 * `Popover` instances side by side). Two such siblings still can't stay
 * open at once today, but only because Radix's own `Popover.Content`
 * `DismissableLayer` closes one when focus moves to the other — an
 * incidental consequence of Radix's focus/dismiss wiring, not something
 * this gate enforces or promises for nested content. A future nested
 * overlay shape that doesn't route through Radix's default dismiss
 * behavior (a `preventAutoFocus`/custom-dismiss combination, say) is not
 * structurally guaranteed to close its siblings the way top-level overlays
 * still are — worth checking live, not assumed from this gate alone, the
 * same way this story's own live-verification step actually is required
 * elsewhere.
 */
export const OverlayContentContext = createContext(false);

/**
 * Grants `open` only when no other overlay currently holds the gate.
 * Each call site gets its own stable token (one per component instance,
 * via `useRef`) so re-renders never fight themselves for ownership, and a
 * closed or unmounted overlay always releases the slot immediately —
 * nothing can wedge the gate for whatever opens next.
 *
 * `useLayoutEffect`, not `useEffect`: the grant/refusal must land before
 * the browser paints, or a genuinely-refused second overlay would flash
 * open for one frame before snapping shut.
 */
export function useOverlayGrant(open: boolean, gate: OverlayGate = appOverlayGate): boolean {
  const tokenRef = useRef<object>({});
  const [granted, setGranted] = useState(false);

  useLayoutEffect(() => {
    if (!open) {
      gate.release(tokenRef.current);
      setGranted(false);
      return;
    }
    // Try now, then keep trying whenever the gate's occupancy changes —
    // covers both directions: a grant that only becomes possible once
    // whatever was blocking it lets go, and (defensively) a grant that
    // ought to be revoked if something else ever won the same slot.
    function attempt() {
      setGranted(gate.requestOpen(tokenRef.current));
    }
    attempt();
    const unsubscribe = gate.subscribe(attempt);
    return () => {
      unsubscribe();
      gate.release(tokenRef.current);
    };
  }, [open, gate]);

  return granted;
}

/**
 * Remembers whatever element had focus the instant `open` became `true`,
 * so it can be restored on close.
 *
 * Radix `Dialog`'s own built-in focus-return (VC-2.6-04's "closing returns
 * focus to whatever opened it") only works when the trigger is rendered as
 * a Radix `Dialog.Trigger` — Radix keeps a ref to it internally and
 * restores focus there on close. `Dialog.tsx`/`Sheet.tsx` are fully
 * controlled from outside (`open`/`onOpenChange` props) precisely so an
 * arbitrary caller-owned button — a table row's ghost "Remove", a chip's
 * "Delete" — can open one without also having to render it *as* a Radix
 * Trigger, so that ref is never populated and Radix's own restoration has
 * nothing to focus (confirmed live: without this hook, focus fell through
 * to `<body>` on close, a real defect this story's own live-verification
 * step exists to catch, not just Radix's problem to solve unassisted).
 * `Dialog.tsx`/`Sheet.tsx` pass the return value's `.current` to their
 * `Content`'s `onCloseAutoFocus`, preventing Radix's default and focusing
 * this element themselves instead.
 */
export function useReturnFocusOnOpen(open: boolean): RefObject<HTMLElement | null> {
  const elementRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      elementRef.current = document.activeElement;
    }
  }, [open]);

  return elementRef;
}

/**
 * The element `Dialog.tsx`/`Sheet.tsx` should actually focus on close:
 * the captured trigger, if it's still there, otherwise a caller-supplied
 * fallback, if that's still there, otherwise `null` (let the browser do
 * whatever it would already do — never worse than the pre-fix behavior).
 *
 * Found live, on the *confirm* path specifically (Cancel had already been
 * checked and looked fine): confirming a removal unmounts the row that
 * held the trigger as part of the very same action, so
 * `useReturnFocusOnOpen`'s captured element is a detached DOM node by the
 * time `onCloseAutoFocus` fires — `.focus()` on a node no longer in the
 * document is a silent no-op, and focus fell through to `<body>` again,
 * the exact defect the hook exists to prevent. `isConnected` is the
 * standard DOM check for "still attached to the document" — cheap, no
 * `document.contains` walk needed, and correct even if the node was
 * detached and never garbage-collected.
 */
export function focusReturnElement(
  returnFocusTo: RefObject<HTMLElement | null>,
  fallbackFocusRef?: RefObject<HTMLElement | null>,
): HTMLElement | null {
  if (returnFocusTo.current?.isConnected) {
    return returnFocusTo.current;
  }
  if (fallbackFocusRef?.current?.isConnected) {
    return fallbackFocusRef.current;
  }
  return null;
}
